import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DogEarCard, IconButton, RuleBicolor } from '@audiq/ui';
import { Archive, Copy, Pencil, Send } from 'lucide-react';
import { api } from '../lib/api';
import { ApiClientError } from '../lib/api/_request';
import type { Flow } from '@audiq/shared';

export function FlowsListPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);

  const flowsQuery = useQuery({
    queryKey: ['flows', { search, includeArchived }],
    queryFn: () =>
      api.listFlows({
        search: search || undefined,
        include_archived: includeArchived,
      }),
  });

  const publishMut = useMutation({
    mutationFn: (id: string) => api.publishFlow(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['flows'] }),
  });
  const archiveMut = useMutation({
    mutationFn: (id: string) => api.deleteFlow(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['flows'] }),
  });
  const duplicateMut = useMutation({
    mutationFn: (id: string) => api.duplicateFlow(id),
    onSuccess: (newFlow) => {
      qc.invalidateQueries({ queryKey: ['flows'] });
      navigate(`/flows/${newFlow.id}`);
    },
  });

  function flowStatus(flow: Flow): 'attivo' | 'draft' | 'archiviato' {
    if (flow.archived_at) return 'archiviato';
    if (flow.is_active) return 'attivo';
    return 'draft';
  }

  return (
    <div className="mx-auto max-w-5xl">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="mono mb-2" style={{ color: 'var(--mute)' }}>
            Configurazione
          </div>
          <h2 className="serif text-4xl" style={{ color: 'var(--ink)' }}>
            Flow
          </h2>
        </div>
        <Link
          to="/flows/new"
          className="rounded-md px-4 py-2 text-sm font-medium transition"
          style={{ background: 'var(--primary)', color: '#fff' }}
        >
          + Nuovo flow
        </Link>
      </div>

      <RuleBicolor className="my-6" />

      <div className="mb-4 flex items-center gap-3">
        <input
          type="search"
          placeholder="Cerca per nome…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 rounded-md border px-3 py-2 text-sm"
          style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
        />
        <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--mute)' }}>
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
          />
          Mostra archiviati
        </label>
      </div>

      {flowsQuery.isLoading && <p style={{ color: 'var(--mute)' }}>Carico…</p>}

      {flowsQuery.data && flowsQuery.data.flows.length === 0 && (
        <DogEarCard className="rounded-md p-8 text-center">
          <h3 className="serif mb-2 text-2xl" style={{ color: 'var(--ink)' }}>
            Niente flow ancora.
          </h3>
          <p className="mb-6 text-sm" style={{ color: 'var(--mute)' }}>
            Inizia da un template o costruisci da zero.
          </p>
          <Link
            to="/flows/new"
            className="inline-block rounded-md px-4 py-2 text-sm font-medium"
            style={{ background: 'var(--primary)', color: '#fff' }}
          >
            Crea il primo flow
          </Link>
        </DogEarCard>
      )}

      {flowsQuery.data && flowsQuery.data.flows.length > 0 && (
        <div
          className="overflow-hidden rounded-md border"
          style={{ borderColor: 'var(--line)', background: 'var(--paper)' }}
        >
          <table className="w-full text-sm">
            <thead style={{ background: 'var(--soft)' }}>
              <tr style={{ color: 'var(--mute)' }} className="mono">
                <th className="ps-4 pe-2 py-2 text-start">Nome</th>
                <th className="px-2 py-2 text-start">Slug</th>
                <th className="px-2 py-2 text-start">Lingua</th>
                <th className="px-2 py-2 text-start">Stato</th>
                <th className="px-2 py-2 text-start">Aggiornato</th>
                <th className="ps-2 pe-4 py-2 text-end">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {flowsQuery.data.flows.map((f) => (
                <tr key={f.id} style={{ borderTop: '1px solid var(--line)', color: 'var(--ink)' }}>
                  <td className="ps-4 pe-2 py-3">
                    <Link
                      to={`/flows/${f.id}`}
                      className="font-medium underline-offset-2 hover:underline"
                      style={{ color: 'var(--primary-2)' }}
                    >
                      {f.name}
                    </Link>
                  </td>
                  <td className="px-2 py-3 font-mono text-xs">{f.slug}</td>
                  <td className="px-2 py-3 mono">{f.language_default}</td>
                  <td className="px-2 py-3">
                    <span
                      className="rounded-full px-2 py-0.5 mono"
                      style={
                        flowStatus(f) === 'attivo'
                          ? {
                              background: 'color-mix(in oklab, var(--primary) 20%, transparent)',
                              color: 'var(--primary-2)',
                            }
                          : flowStatus(f) === 'archiviato'
                            ? { background: 'var(--soft)', color: 'var(--mute)' }
                            : {
                                background: 'color-mix(in oklab, var(--accent) 20%, transparent)',
                                color: 'var(--ink)',
                              }
                      }
                    >
                      {flowStatus(f)}
                    </span>
                  </td>
                  <td className="px-2 py-3 mono" style={{ color: 'var(--mute)' }}>
                    {new Date(f.updated_at).toLocaleDateString('it-IT')}
                  </td>
                  <td className="ps-2 pe-4 py-3 text-end">
                    <div className="flex items-center justify-end gap-1">
                      <IconButton
                        as="a"
                        href={`/flows/${f.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(`/flows/${f.id}`);
                        }}
                        icon={Pencil}
                        label="Modifica"
                      />
                      <IconButton
                        icon={Copy}
                        label="Duplica"
                        onClick={() => duplicateMut.mutate(f.id)}
                        disabled={duplicateMut.isPending}
                      />
                      {flowStatus(f) === 'draft' && (
                        <IconButton
                          icon={Send}
                          label="Pubblica"
                          onClick={() => publishMut.mutate(f.id)}
                          disabled={publishMut.isPending}
                        />
                      )}
                      {flowStatus(f) !== 'archiviato' && (
                        <IconButton
                          icon={Archive}
                          label="Archivia"
                          tone="danger"
                          onClick={() => {
                            if (confirm(`Archiviare "${f.name}"?`)) archiveMut.mutate(f.id);
                          }}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {publishMut.isError && (
        <p className="mt-4 text-sm" style={{ color: 'var(--danger)' }}>
          {publishMut.error instanceof ApiClientError
            ? publishMut.error.payload.error
            : 'Errore pubblicazione'}
        </p>
      )}
    </div>
  );
}
